import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Link, Popover, Stack, Tooltip, Typography } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import { ListAccessState } from '@/components/shared/AccessState/DataBlocks';

import type { NotificationView } from './api';
import { NOTIFICATIONS_PATH, NOTIFICATION_SETTINGS_PATH, VIEW_LABEL } from './format';
import { NotificationGroups, NothingHere } from './NotificationList';
import { markAllRead, useNotificationList, useNotificationSummary } from './useNotifications';
import { useOpenNotification } from './useOpenNotification';

/** How many items the panel shows; the page has the rest. */
export const PANEL_LIMIT = 20;

const VIEWS: NotificationView[] = ['all', 'action', 'unread'];

/** The tab pills of the panel and the page: "All", "Needs action (2)", "Unread (3)". */
export function ViewTabs({
  value,
  onChange,
  testId,
}: {
  value: NotificationView;
  // eslint-disable-next-line no-unused-vars
  onChange: (view: NotificationView) => void;
  testId: string;
}) {
  const { unread, needsAction } = useNotificationSummary();
  const counts: Record<NotificationView, number | null> = {
    all: null,
    action: needsAction,
    unread,
  };
  return (
    <Stack direction='row' spacing={0.75} role='tablist' aria-label='Show' data-testid={testId}>
      {VIEWS.map((view) => {
        const on = value === view;
        const n = counts[view];
        return (
          <Button
            key={view}
            role='tab'
            aria-selected={on}
            size='small'
            onClick={() => onChange(view)}
            data-testid={`${testId}-${view}`}
            sx={{
              height: 30,
              px: 1.5,
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'none',
              border: '1px solid',
              borderColor: on ? 'secondary.main' : '#d5dbe2',
              bgcolor: on ? '#e3f0fd' : 'background.paper',
              color: on ? '#0d47a1' : '#3b4450',
            }}
          >
            {n === null ? VIEW_LABEL[view] : `${VIEW_LABEL[view]} (${n})`}
          </Button>
        );
      })}
    </Stack>
  );
}

/**
 * The bell's panel beside the sidebar (mock-up "Notifications", owner change 1): tabs All /
 * Needs action / Unread, "Mark all as read", the gear to Notification settings and the link
 * to the full Notifications page. While open its list asks again every 15 s (5 s with an item
 * in progress, at once when the unread count goes up); closed, it asks nothing.
 */
export function NotificationPanel({
  anchorTop,
  onClose,
}: {
  /** the bottom edge of the sidebar entry: the panel's bottom lines up with it; null = closed */
  anchorTop: number | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const open = anchorTop !== null;
  const [view, setView] = useState<NotificationView>('all');
  const list = useNotificationList({ view, limit: PANEL_LIMIT, enabled: open });
  const openItem = useOpenNotification(onClose);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference='anchorPosition'
      // beside the 65px sidebar, like the role menu
      anchorPosition={{ top: anchorTop ?? 0, left: 73 }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      sx={{ zIndex: 1201 }}
      slotProps={{
        paper: {
          'aria-label': 'Notifications',
          role: 'dialog',
          sx: {
            width: 440,
            maxWidth: 'calc(100vw - 89px)',
            maxHeight: 'min(780px, calc(100vh - 32px))',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 12px 32px rgba(31,42,55,0.14)',
          },
        } as object,
      }}
    >
      <Box data-testid='notification-panel' sx={{ display: 'contents' }}>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ px: 2, pt: 2, pb: 1 }}
        >
          <Typography component='h2' sx={{ fontSize: '17px', fontWeight: 700 }}>
            Notifications
          </Typography>
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <Button
              size='small'
              color='secondary'
              onClick={() => void markAllRead()}
              data-testid='notification-panel-read-all'
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Mark all as read
            </Button>
            <Tooltip title='Notification settings'>
              <IconButton
                size='small'
                aria-label='Notification settings'
                onClick={() => go(NOTIFICATION_SETTINGS_PATH)}
                data-testid='notification-panel-settings'
              >
                <SettingsOutlinedIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Box sx={{ px: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <ViewTabs value={view} onChange={setView} testId='notification-panel-tab' />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <ListAccessState state={list.state} rows={4} columns={1} empty={<NothingHere />}>
            {() => (
              <NotificationGroups
                items={list.items}
                onOpen={openItem}
                testId='notification-panel-item'
              />
            )}
          </ListAccessState>
        </Box>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          spacing={1.5}
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafbfc',
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: '12px' }}>
            Only you see these. Choose what you get in{' '}
            <Link
              component={RouterLink}
              to={NOTIFICATION_SETTINGS_PATH}
              onClick={onClose}
              color='secondary'
            >
              Notification settings
            </Link>
            .
          </Typography>
          <Link
            component={RouterLink}
            to={NOTIFICATIONS_PATH}
            onClick={onClose}
            color='secondary'
            underline='hover'
            data-testid='notification-panel-all'
            sx={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            See all
          </Link>
        </Stack>
      </Box>
    </Popover>
  );
}
